import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  LinearProgress,
  Button,
} from '@mui/material';
import {
  ChatBubbleOutline as ChatIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Save as SaveIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import Markdown from 'react-markdown';
import { DesignSpec } from '../../../services/workflow';
import { exportDesignSpecToPdf } from '../../../utils/pdfExport';

// Types for section feedback
export interface SectionFeedback {
  section: string;
  score: number;
  suggestion: string;
  is_weak: boolean;
}

interface DesignSpecEditorProps {
  spec: DesignSpec | null;
  onSave?: (spec: DesignSpec) => Promise<void>;
  onAskAboutSection?: (section: string, prompt: string) => void;
  sectionFeedback?: SectionFeedback[];
  qualityScore?: number;
  qualityScoreLabel?: string;
  isLoading?: boolean;
  isSaving?: boolean;
}

// Section configuration
type SectionGroup =
  | 'identity'
  | 'goals'
  | 'capabilities'
  | 'behavioral'
  | 'integrations'
  | 'operations'
  | 'failures'
  | 'business'
  | 'architecture';

interface SectionConfig {
  key: keyof DesignSpec;
  label: string;
  group: SectionGroup;
  placeholder: string;
  prompt: string;
  multiline?: boolean;
  isArray?: boolean;
  // For nested object fields
  nestedKey?: string;
  nestedType?: 'string' | 'array' | 'number';
  // For key-value map fields
  isKeyValueMap?: boolean;
  keyLabel?: string;
  valueLabel?: string;
  valueType?: 'string' | 'number' | 'array';
}

const SECTIONS: SectionConfig[] = [
  // Agent Identity group
  {
    key: 'agent_name',
    label: 'Agent Name',
    group: 'identity',
    placeholder: '[To be defined] - A short, memorable name for the agent',
    prompt: 'Help me create a clear, memorable name for this agent',
  },
  {
    key: 'persona',
    label: 'Persona',
    group: 'identity',
    placeholder:
      "[To be defined] - Describe the agent's personality and expertise",
    prompt: 'Help me define a compelling persona for this agent',
    multiline: true,
  },
  {
    key: 'tone',
    label: 'Communication Tone',
    group: 'identity',
    placeholder: '[To be defined] - How should the agent communicate?',
    prompt: 'Help me define the right communication tone for this agent',
  },

  // Goals group
  {
    key: 'primary_goal',
    label: 'Primary Goal',
    group: 'goals',
    placeholder:
      '[To be defined] - The main objective this agent will accomplish',
    prompt: 'Help me articulate a clear primary goal for this agent',
    multiline: true,
  },
  {
    key: 'secondary_goals',
    label: 'Secondary Goals',
    group: 'goals',
    placeholder: '[To be defined]',
    prompt: 'Help me identify additional goals for this agent',
    isArray: true,
  },

  // Capabilities group
  {
    key: 'capabilities',
    label: 'Capabilities',
    group: 'capabilities',
    placeholder: '[To be defined]',
    prompt: 'Help me define what this agent should be able to do',
    isArray: true,
  },
  {
    key: 'boundaries',
    label: 'Boundaries',
    group: 'capabilities',
    placeholder: '[To be defined]',
    prompt: 'Help me identify what this agent should NOT do',
    isArray: true,
  },

  // Behavioral Guidelines group
  {
    key: 'autonomy_level',
    label: 'Autonomy Level',
    group: 'behavioral',
    placeholder:
      '[To be defined] - How independently should the agent operate?',
    prompt: 'Help me define the right level of autonomy for this agent',
    multiline: true,
  },
  {
    key: 'error_handling',
    label: 'Error Handling',
    group: 'behavioral',
    placeholder:
      '[To be defined] - How should the agent handle errors and edge cases?',
    prompt: 'Help me define error handling behavior for this agent',
    multiline: true,
  },

  // Integrations group
  {
    key: 'integrations',
    label: 'Required Integrations',
    group: 'integrations',
    placeholder: '[To be defined]',
    prompt: 'Help me identify required integrations for this agent',
    isArray: true,
  },

  // Operations group - Input Sources (nested fields)
  {
    key: 'input_sources',
    label: 'User Inputs',
    group: 'operations',
    placeholder: 'e.g., Chat messages, Form submissions',
    prompt: 'Help me define user input sources',
    nestedKey: 'user_inputs',
    nestedType: 'array',
  },
  {
    key: 'input_sources',
    label: 'System Events',
    group: 'operations',
    placeholder: 'e.g., Scheduled triggers, Webhooks',
    prompt: 'Help me define system event triggers',
    nestedKey: 'system_events',
    nestedType: 'array',
  },
  {
    key: 'input_sources',
    label: 'External Triggers',
    group: 'operations',
    placeholder: 'e.g., API calls, Email notifications',
    prompt: 'Help me define external triggers',
    nestedKey: 'external_triggers',
    nestedType: 'array',
  },
  {
    key: 'output_types',
    label: 'Output Types',
    group: 'operations',
    placeholder: '[To be defined]',
    prompt: 'Help me define what outputs this agent produces',
    isArray: true,
  },

  // Handoff Rules (nested fields)
  {
    key: 'handoff_rules',
    label: 'Escalation Triggers',
    group: 'operations',
    placeholder: 'e.g., Customer anger detected, Complex request',
    prompt: 'Help me define when to escalate to humans',
    nestedKey: 'triggers',
    nestedType: 'array',
  },
  {
    key: 'handoff_rules',
    label: 'Escalation Targets',
    group: 'operations',
    placeholder: 'e.g., Support Team, Technical Lead',
    prompt: 'Help me define who to escalate to',
    nestedKey: 'targets',
    nestedType: 'array',
  },
  {
    key: 'handoff_rules',
    label: 'Handoff Payload Fields',
    group: 'operations',
    placeholder: 'e.g., Conversation history, Customer ID',
    prompt: 'Help me define what data to include in handoffs',
    nestedKey: 'payload_fields',
    nestedType: 'array',
  },

  // Failures group (nested fields)
  {
    key: 'failure_modes',
    label: 'Missing Data Response',
    group: 'failures',
    placeholder: 'What should the agent do when required data is missing?',
    prompt: 'Help me define how to handle missing data',
    nestedKey: 'missing_data',
    nestedType: 'string',
    multiline: true,
  },
  {
    key: 'failure_modes',
    label: 'Tool/API Failure Response',
    group: 'failures',
    placeholder: 'What should the agent do when tools or APIs fail?',
    prompt: 'Help me define how to handle tool failures',
    nestedKey: 'tool_failure',
    nestedType: 'string',
    multiline: true,
  },
  {
    key: 'failure_modes',
    label: 'Unclear Intent Response',
    group: 'failures',
    placeholder: "What should the agent do when user intent isn't clear?",
    prompt: 'Help me define how to handle unclear requests',
    nestedKey: 'unclear_intent',
    nestedType: 'string',
    multiline: true,
  },
  {
    key: 'failure_modes',
    label: 'Rate Limited Response',
    group: 'failures',
    placeholder: 'What should the agent do when rate limited?',
    prompt: 'Help me define how to handle rate limiting',
    nestedKey: 'rate_limited',
    nestedType: 'string',
    multiline: true,
  },

  // Business group (nested fields)
  {
    key: 'business_assumptions',
    label: 'Expected Users',
    group: 'business',
    placeholder: 'Number of expected users',
    prompt: 'Help me estimate the expected user count',
    nestedKey: 'expected_users',
    nestedType: 'number',
  },
  {
    key: 'business_assumptions',
    label: 'Usage Per User (monthly)',
    group: 'business',
    placeholder: 'Average interactions per user per month',
    prompt: 'Help me estimate usage frequency',
    nestedKey: 'usage_per_user',
    nestedType: 'number',
  },
  {
    key: 'business_assumptions',
    label: 'Growth Rate',
    group: 'business',
    placeholder: 'e.g., 10% monthly, 2x yearly',
    prompt: 'Help me estimate growth trajectory',
    nestedKey: 'growth_rate',
    nestedType: 'string',
  },
  {
    key: 'business_assumptions',
    label: 'Value Per Task',
    group: 'business',
    placeholder: 'e.g., $5 saved per ticket, 10 min saved per query',
    prompt: 'Help me estimate the value delivered per task',
    nestedKey: 'value_per_task',
    nestedType: 'string',
  },

  // Architecture group (key-value maps)
  {
    key: 'capability_dependencies',
    label: 'Capability Dependencies',
    group: 'architecture',
    placeholder: 'Format: Capability → Dependency1, Dependency2 (one per line)',
    prompt: 'Help me identify which capabilities depend on others',
    isKeyValueMap: true,
    keyLabel: 'Capability',
    valueLabel: 'Dependencies',
    valueType: 'array',
  },
  {
    key: 'capability_slas',
    label: 'Response Time SLAs',
    group: 'architecture',
    placeholder: 'Format: Capability → Time in seconds (one per line)',
    prompt: 'Help me define response time targets for each capability',
    isKeyValueMap: true,
    keyLabel: 'Capability',
    valueLabel: 'SLA (seconds)',
    valueType: 'number',
  },
];

// Group labels for visual organization
const GROUP_LABELS: Record<SectionGroup, string> = {
  identity: 'Agent Identity',
  goals: 'Goals',
  capabilities: 'Capabilities & Boundaries',
  behavioral: 'Behavioral Guidelines',
  integrations: 'Integrations',
  operations: 'Operational Specification',
  failures: 'Failure Handling',
  business: 'Business Context',
  architecture: 'Architecture Insights',
};

// Convert spec to markdown
const specToMarkdown = (spec: DesignSpec): string => {
  const formatList = (items: string[] | undefined): string => {
    if (!items || !Array.isArray(items) || items.length === 0)
      return '- [To be defined]';
    return items.map((item) => `- ${item}`).join('\n');
  };

  // Format input sources
  const inputSources = spec.input_sources;
  const userInputs = formatList(inputSources?.user_inputs);
  const systemEvents = formatList(inputSources?.system_events);
  const externalTriggers = formatList(inputSources?.external_triggers);

  // Format handoff rules
  const handoffs = spec.handoff_rules;
  const handoffTriggers = formatList(handoffs?.triggers);
  const handoffTargets = formatList(handoffs?.targets);

  // Format failure modes
  const failures = spec.failure_modes;

  // Format business assumptions
  const bizAssumptions = spec.business_assumptions;
  const bizSection = bizAssumptions
    ? `- **Expected Users:** ${bizAssumptions.expected_users || 'Not specified'}
- **Usage Per User (monthly):** ${bizAssumptions.usage_per_user || 'Not specified'}
- **Growth Rate:** ${bizAssumptions.growth_rate || 'Not specified'}
- **Value Per Task:** ${bizAssumptions.value_per_task || 'Not specified'}`
    : '- [To be defined]';

  // Format capability dependencies
  const capDeps = spec.capability_dependencies;
  const capDepsSection =
    capDeps && Object.keys(capDeps).length > 0
      ? Object.entries(capDeps)
          .map(([cap, deps]) => `- **${cap}** → ${deps.join(', ')}`)
          .join('\n')
      : '- [No dependencies defined]';

  // Format capability SLAs
  const capSlas = spec.capability_slas;
  const capSlasSection =
    capSlas && Object.keys(capSlas).length > 0
      ? Object.entries(capSlas)
          .map(([cap, sla]) => `- **${cap}:** ${sla}s`)
          .join('\n')
      : '- [No SLAs defined]';

  return `# Agent Design Specification

## Agent Identity

### Name
**${spec.agent_name || '[To be defined]'}**

### Persona
${spec.persona || '[To be defined]'}

### Communication Tone
${spec.tone || '[To be defined]'}

---

## Goals

### Primary Goal
${spec.primary_goal || '[To be defined]'}

### Secondary Goals
${formatList(spec.secondary_goals)}

---

## Capabilities

### What This Agent Can Do
${formatList(spec.capabilities)}

### Boundaries (What It Should NOT Do)
${formatList(spec.boundaries)}

---

## Behavioral Guidelines

### Autonomy Level
${spec.autonomy_level || '[To be defined]'}

### Error Handling
${spec.error_handling || '[To be defined]'}

---

## Integrations
${formatList(spec.integrations)}

---

## Input Sources

### User Inputs
${userInputs}

### System Events
${systemEvents}

### External Triggers
${externalTriggers}

---

## Output Types
${formatList(spec.output_types)}

---

## Human Handoff Rules

### When to Escalate
${handoffTriggers}

### Escalation Targets
${handoffTargets}

---

## Failure Handling

**Missing Data**
${failures?.missing_data || '[To be defined]'}

**Tool/API Failure**
${failures?.tool_failure || '[To be defined]'}

**Unclear Intent**
${failures?.unclear_intent || '[To be defined]'}

**Rate Limited**
${failures?.rate_limited || '[To be defined]'}

---

## Business Assumptions
${bizSection}

---

## Capability Dependencies
${capDepsSection}

---

## Response Time SLAs
${capSlasSection}

---
*Generated by Design Expert*
`;
};

// Get feedback for a section
const getFeedbackForSection = (
  sectionKey: string,
  feedback: SectionFeedback[]
): SectionFeedback | undefined => {
  return feedback.find((f) => f.section === sectionKey);
};

// Section header with status indicator
const SectionHeader: React.FC<{
  label: string;
  feedback?: SectionFeedback;
  onAskClick?: () => void;
}> = ({ label, feedback, onAskClick }) => {
  const isWeak = feedback?.is_weak ?? false;
  const score = feedback?.score ?? 1;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle2" fontWeight="bold">
          {label}
        </Typography>
        {score >= 0.7 ? (
          <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
        ) : (
          <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />
        )}
      </Box>
      {onAskClick && isWeak && (
        <Tooltip title="Ask agent about this section">
          <IconButton size="small" onClick={onAskClick} sx={{ p: 0.5 }}>
            <ChatIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

// Inline hint for weak sections
const InlineHint: React.FC<{ feedback?: SectionFeedback }> = ({ feedback }) => {
  if (!feedback?.is_weak || !feedback?.suggestion) return null;

  return (
    <Typography
      variant="caption"
      sx={{
        color: 'warning.dark',
        display: 'block',
        mt: 0.5,
        fontStyle: 'italic',
      }}
    >
      {feedback.suggestion}
    </Typography>
  );
};

// Helper to compare specs for equality (deep comparison of all 17 fields)
const specsAreEqual = (a: DesignSpec | null, b: DesignSpec | null): boolean => {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  // Use JSON.stringify for deep comparison of all fields
  return JSON.stringify(a) === JSON.stringify(b);
};

// Helper to format key-value map for display
const formatKeyValueMap = (
  map: Record<string, string[] | number> | undefined,
  valueType: 'array' | 'number'
): string => {
  if (!map || Object.keys(map).length === 0) return '';
  return Object.entries(map)
    .map(([key, value]) => {
      if (valueType === 'array') {
        return `${key} → ${(value as string[]).join(', ')}`;
      }
      return `${key} → ${value}`;
    })
    .join('\n');
};

// Helper to parse key-value map from text
const parseKeyValueMap = (
  text: string,
  valueType: 'array' | 'number'
): Record<string, string[] | number> => {
  const result: Record<string, string[] | number> = {};
  text.split('\n').forEach((line) => {
    const match = line.match(/^(.+?)\s*[→:]\s*(.+)$/);
    if (match) {
      const key = match[1].trim();
      const valueStr = match[2].trim();
      if (valueType === 'array') {
        result[key] = valueStr.split(',').map((s) => s.trim());
      } else {
        const num = parseFloat(valueStr);
        if (!isNaN(num)) {
          result[key] = num;
        }
      }
    }
  });
  return result;
};

const DesignSpecEditor: React.FC<DesignSpecEditorProps> = ({
  spec,
  onSave,
  onAskAboutSection,
  sectionFeedback = [],
  qualityScore,
  qualityScoreLabel = 'Completeness',
  isLoading = false,
  isSaving = false,
}) => {
  const [tab, setTab] = useState(0);
  const [localSpec, setLocalSpec] = useState<DesignSpec | null>(spec);
  const [savedSpec, setSavedSpec] = useState<DesignSpec | null>(spec);
  const lastExternalSpec = useRef<DesignSpec | null>(spec);

  // Sync local spec when parent sends a new spec (from agent responses)
  useEffect(() => {
    if (spec !== lastExternalSpec.current) {
      lastExternalSpec.current = spec;
      setLocalSpec(spec);
      setSavedSpec(spec);
    }
  }, [spec]);

  const hasUnsavedChanges = !specsAreEqual(localSpec, savedSpec);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  // Handle field changes
  const handleFieldChange = useCallback(
    (field: keyof DesignSpec, value: string | string[]) => {
      setLocalSpec((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          [field]: value,
        };
      });
    },
    []
  );

  // Handle list field changes for array fields
  const handleListFieldChange = useCallback(
    (
      field:
        | 'secondary_goals'
        | 'capabilities'
        | 'boundaries'
        | 'integrations'
        | 'output_types',
      text: string
    ) => {
      setLocalSpec((prev) => {
        if (!prev) return prev;
        const items = text
          .split('\n')
          .map((s) => s.replace(/^-\s*/, '').trim())
          .filter(Boolean);
        return {
          ...prev,
          [field]: items,
        };
      });
    },
    []
  );

  // Handle nested object field changes (input_sources, handoff_rules, failure_modes, business_assumptions)
  const handleNestedFieldChange = useCallback(
    (
      field: keyof DesignSpec,
      nestedKey: string,
      value: string | string[] | number
    ) => {
      setLocalSpec((prev) => {
        if (!prev) return prev;
        const currentObj =
          (prev[field] as Record<string, unknown> | undefined) || {};
        return {
          ...prev,
          [field]: {
            ...currentObj,
            [nestedKey]: value,
          },
        };
      });
    },
    []
  );

  // Handle nested array field changes (input_sources.user_inputs, etc.)
  const handleNestedArrayChange = useCallback(
    (field: keyof DesignSpec, nestedKey: string, text: string) => {
      const items = text
        .split('\n')
        .map((s) => s.replace(/^-\s*/, '').trim())
        .filter(Boolean);
      handleNestedFieldChange(field, nestedKey, items);
    },
    [handleNestedFieldChange]
  );

  // Handle key-value map field changes (capability_dependencies, capability_slas)
  const handleKeyValueMapChange = useCallback(
    (
      field: 'capability_dependencies' | 'capability_slas',
      text: string,
      valueType: 'array' | 'number'
    ) => {
      setLocalSpec((prev) => {
        if (!prev) return prev;
        const parsed = parseKeyValueMap(text, valueType);
        return {
          ...prev,
          [field]: parsed,
        };
      });
    },
    []
  );

  // Handle save
  const handleSave = useCallback(async () => {
    if (!localSpec || !onSave) return;
    await onSave(localSpec);
    setSavedSpec(localSpec);
  }, [localSpec, onSave]);

  // Empty state
  if (!localSpec) {
    return (
      <Paper
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="body2" color="text.secondary" textAlign="center">
          The Design agent will generate a specification based on your problem
          statement.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          The draft will appear here shortly.
        </Typography>
      </Paper>
    );
  }

  const markdown = specToMarkdown(localSpec);

  // Group sections for rendering
  const groupedSections = SECTIONS.reduce(
    (acc, section) => {
      if (!acc[section.group]) acc[section.group] = [];
      acc[section.group].push(section);
      return acc;
    },
    {} as Record<string, SectionConfig[]>
  );

  return (
    <Paper
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header with quality score */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          pt: 1,
          pb: 0.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Agent Design Specification
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {qualityScore !== undefined && (
              <Chip
                label={`${qualityScoreLabel}: ${Math.round(qualityScore * 100)}%`}
                size="small"
                color={
                  qualityScore >= 0.8
                    ? 'success'
                    : qualityScore >= 0.5
                      ? 'warning'
                      : 'default'
                }
                variant="outlined"
              />
            )}
            <Tooltip title="Export as PDF">
              <IconButton
                size="small"
                onClick={() => exportDesignSpecToPdf(localSpec!)}
                disabled={!localSpec}
              >
                <PdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {qualityScore !== undefined && (
          <LinearProgress
            variant="determinate"
            value={qualityScore * 100}
            sx={{
              height: 4,
              borderRadius: 2,
              mb: 1,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                bgcolor:
                  qualityScore >= 0.8
                    ? 'success.main'
                    : qualityScore >= 0.5
                      ? 'warning.main'
                      : 'grey.400',
              },
            }}
          />
        )}
        <Tabs
          value={tab}
          onChange={handleTabChange}
          aria-label="design spec editor tabs"
        >
          <Tab label="Preview" />
          <Tab label="Edit" />
        </Tabs>
      </Box>

      {/* Loading indicator */}
      {isLoading && <LinearProgress />}

      {/* Content area */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {tab === 0 ? (
          // Preview tab - styled markdown
          <Box
            className="markdown-preview"
            sx={{
              '& h1': {
                fontSize: '1.5rem',
                mb: 2,
                color: 'primary.main',
                fontWeight: 600,
              },
              '& h2': {
                fontSize: '1.2rem',
                mt: 3,
                mb: 1,
                color: 'text.primary',
                borderBottom: 1,
                borderColor: 'divider',
                pb: 0.5,
                fontWeight: 600,
              },
              '& h3': { fontSize: '1rem', mt: 2, mb: 0.5, fontWeight: 600 },
              '& p': { mb: 1, lineHeight: 1.6 },
              '& ul': { pl: 2, mb: 1 },
              '& li': { mb: 0.5 },
              '& hr': { my: 2, borderColor: 'divider' },
              '& strong': { fontWeight: 600 },
            }}
          >
            <Markdown>{markdown}</Markdown>
          </Box>
        ) : (
          // Edit tab - form fields grouped by category
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {Object.entries(groupedSections).map(([group, sections]) => (
              <Box key={group}>
                {/* Group header */}
                <Typography
                  variant="overline"
                  sx={{
                    display: 'block',
                    color: 'text.secondary',
                    borderBottom: 1,
                    borderColor: 'divider',
                    pb: 0.5,
                    mb: 2,
                    letterSpacing: 1,
                  }}
                >
                  {GROUP_LABELS[group as SectionGroup]}
                </Typography>

                {/* Section fields */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {sections.map((section, index) => {
                    const feedback = getFeedbackForSection(
                      section.key,
                      sectionFeedback
                    );
                    const rawValue = localSpec[section.key];

                    // Generate unique key for sections with same key but different nestedKey
                    const sectionUniqueKey = section.nestedKey
                      ? `${section.key}.${section.nestedKey}`
                      : section.isKeyValueMap
                        ? `${section.key}.kv.${index}`
                        : section.key;

                    // Determine the display value based on field type
                    let displayValue = '';
                    if (section.isKeyValueMap) {
                      // Key-value map fields
                      displayValue = formatKeyValueMap(
                        rawValue as
                          | Record<string, string[] | number>
                          | undefined,
                        section.valueType as 'array' | 'number'
                      );
                    } else if (section.nestedKey) {
                      // Nested object fields
                      const nestedObj = rawValue as
                        | Record<string, unknown>
                        | undefined;
                      const nestedValue = nestedObj?.[section.nestedKey];
                      if (section.nestedType === 'array') {
                        displayValue = Array.isArray(nestedValue)
                          ? nestedValue.map((i) => `- ${i}`).join('\n')
                          : '';
                      } else if (section.nestedType === 'number') {
                        displayValue =
                          nestedValue !== undefined ? String(nestedValue) : '';
                      } else {
                        displayValue = (nestedValue as string) || '';
                      }
                    } else if (section.isArray) {
                      // Top-level array fields
                      displayValue = Array.isArray(rawValue)
                        ? rawValue.map((i) => `- ${i}`).join('\n')
                        : '';
                    } else {
                      // Simple string fields
                      displayValue = (rawValue as string) || '';
                    }

                    // Determine if multiline is needed
                    const isMultiline =
                      section.multiline ||
                      section.isArray ||
                      section.nestedType === 'array' ||
                      section.isKeyValueMap;

                    return (
                      <Box key={sectionUniqueKey}>
                        <SectionHeader
                          label={section.label}
                          feedback={feedback}
                          onAskClick={
                            onAskAboutSection && feedback?.is_weak
                              ? () =>
                                  onAskAboutSection(section.key, section.prompt)
                              : undefined
                          }
                        />
                        <TextField
                          fullWidth
                          multiline={isMultiline}
                          minRows={
                            section.isKeyValueMap
                              ? 3
                              : section.isArray ||
                                  section.nestedType === 'array'
                                ? 2
                                : section.multiline
                                  ? 3
                                  : 1
                          }
                          maxRows={8}
                          size="small"
                          placeholder={section.placeholder}
                          type={
                            section.nestedType === 'number' ? 'number' : 'text'
                          }
                          value={displayValue}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            if (section.isKeyValueMap) {
                              // Key-value map change
                              handleKeyValueMapChange(
                                section.key as
                                  | 'capability_dependencies'
                                  | 'capability_slas',
                                newValue,
                                section.valueType as 'array' | 'number'
                              );
                            } else if (section.nestedKey) {
                              // Nested field change
                              if (section.nestedType === 'array') {
                                handleNestedArrayChange(
                                  section.key,
                                  section.nestedKey,
                                  newValue
                                );
                              } else if (section.nestedType === 'number') {
                                const num = parseFloat(newValue);
                                handleNestedFieldChange(
                                  section.key,
                                  section.nestedKey,
                                  isNaN(num) ? 0 : num
                                );
                              } else {
                                handleNestedFieldChange(
                                  section.key,
                                  section.nestedKey,
                                  newValue
                                );
                              }
                            } else if (section.isArray) {
                              // Top-level array change
                              handleListFieldChange(
                                section.key as
                                  | 'secondary_goals'
                                  | 'capabilities'
                                  | 'boundaries'
                                  | 'integrations'
                                  | 'output_types',
                                newValue
                              );
                            } else {
                              // Simple field change
                              handleFieldChange(section.key, newValue);
                            }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: feedback?.is_weak
                                ? 'warning.50'
                                : 'background.paper',
                            },
                          }}
                        />
                        <InlineHint feedback={feedback} />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}

            {/* Save button */}
            {onSave && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges || isSaving}
                >
                  {isSaving
                    ? 'Saving...'
                    : hasUnsavedChanges
                      ? 'Save Changes'
                      : 'Saved'}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default DesignSpecEditor;
